import { useCallback, useEffect, useRef, useState } from "react";
import { database, functions } from "../../firebase/config";
import { useAuthContext } from "../../hooks/useAuthContext";
import "./Timbratore.css";

const OPERATIONS = [
  {
    value: "open",
    title: "Entra",
  },
  {
    value: "close",
    title: "Esci",
  },
];

const REAR_CAMERA_LABEL_REGEX =
  /(back|rear|environment|wide|triple|dual|main|world)/i;
const SECONDARY_REAR_CAMERA_LABEL_REGEX = /(ultra|tele)/i;
const FRONT_CAMERA_LABEL_REGEX = /(front|user|facetime)/i;

const getCameraId = (camera) => camera?.id || camera?.deviceId || null;
const hasVisibleCameraLabels = (cameras = []) =>
  cameras.some((camera) => String(camera?.label || "").trim());

const choosePreferredCamera = (cameras = []) => {
  if (!Array.isArray(cameras) || !cameras.length) {
    return null;
  }

  if (!hasVisibleCameraLabels(cameras)) {
    return null;
  }

  const primaryRearCamera = cameras.find((camera) =>
    REAR_CAMERA_LABEL_REGEX.test(camera?.label || ""),
  );
  if (primaryRearCamera) {
    return getCameraId(primaryRearCamera);
  }

  const secondaryRearCamera = cameras.find((camera) =>
    SECONDARY_REAR_CAMERA_LABEL_REGEX.test(camera?.label || ""),
  );
  if (secondaryRearCamera) {
    return getCameraId(secondaryRearCamera);
  }

  const nonFrontCameras = cameras.filter(
    (camera) => !FRONT_CAMERA_LABEL_REGEX.test(camera?.label || ""),
  );
  if (nonFrontCameras.length) {
    return getCameraId(nonFrontCameras[nonFrontCameras.length - 1]);
  }

  if (cameras.length > 1) {
    return getCameraId(cameras[cameras.length - 1]);
  }

  return getCameraId(cameras[0]);
};

const waitForNextPaint = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(resolve);
    });
  });

const getAvailableVideoInputs = async () => {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.enumerateDevices !== "function"
  ) {
    return [];
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === "videoinput")
      .map((device) => ({
        id: device.deviceId,
        label: device.label || "",
      }));
  } catch {
    return [];
  }
};

const getRearFacingStream = async (requestedCameraId = "") => {
  const cameras = await getAvailableVideoInputs();
  const preferredCameraId = requestedCameraId || choosePreferredCamera(cameras);
  const streamAttempts = [
    preferredCameraId
      ? {
          video: {
            deviceId: { exact: preferredCameraId },
          },
          audio: false,
        }
      : null,
    {
      video: {
        facingMode: { exact: "environment" },
      },
      audio: false,
    },
    {
      video: {
        facingMode: { ideal: "environment" },
      },
      audio: false,
    },
  ].filter(Boolean);

  let lastError = null;

  for (const constraints of streamAttempts) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to access rear camera");
};

const SUBMIT_LOCK_DURATION_MS = 5000;
const REQUIRED_DAILY_HOURS = 8;

const parsePauseHours = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const normalized =
    typeof value === "number" ? String(value) : String(value).replace(",", ".");
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const formatDurationForMessage = (hours) => {
  const totalMinutes = Math.max(0, Math.round(hours * 60));
  const displayHours = Math.floor(totalMinutes / 60);
  const displayMinutes = totalMinutes % 60;

  if (displayHours > 0 && displayMinutes > 0) {
    return `${displayHours}h ${displayMinutes}m`;
  }

  if (displayHours > 0) {
    return `${displayHours}h`;
  }

  return `${displayMinutes}m`;
};

export default function Timbratore() {
  const { user } = useAuthContext();
  const [selectedOperation, setSelectedOperation] = useState("");
  const [scannerError, setScannerError] = useState("");
  const [scannerStatus, setScannerStatus] = useState("idle");
  const [scannerMode, setScannerMode] = useState("");
  const [cameraPermissionState, setCameraPermissionState] = useState("idle");
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [activeCameraId, setActiveCameraId] = useState("");
  const [successModal, setSuccessModal] = useState(null);
  const [result, setResult] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const detectorRef = useRef(null);
  const html5ScannerRef = useRef(null);
  const isDetectingRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const lockTimeoutRef = useRef(null);

  const stopScanner = useCallback(() => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (lockTimeoutRef.current) {
      clearTimeout(lockTimeoutRef.current);
      lockTimeoutRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (html5ScannerRef.current) {
      const scanner = html5ScannerRef.current;
      html5ScannerRef.current = null;
      Promise.resolve(scanner.stop?.())
        .catch(() => null)
        .finally(() => {
          const container = document.getElementById("timbratore-html5-reader");
          if (container) {
            container.innerHTML = "";
          }
        });
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    isDetectingRef.current = false;
    isSubmittingRef.current = false;
    setActiveCameraId("");
    setScannerMode("");
    setScannerStatus("idle");
  }, []);

  const parseSessionId = useCallback((rawValue) => {
    const value = String(rawValue || "").trim();
    if (!value) return "";

    if (value.startsWith("{")) {
      try {
        const parsed = JSON.parse(value);
        return String(parsed?.sessionId || "").trim();
      } catch (error) {
        return "";
      }
    }

    return value;
  }, []);

  const loadCloseCheckContext = useCallback(async () => {
    if (!user?.uid) {
      return {
        requiresCheck: false,
        pauseHours: 0,
        openTimbratura: null,
      };
    }

    const [userSnapshot, timbraturaSnapshot] = await Promise.all([
      database.collection("users").doc(user.uid).get(),
      database
        .collection("timbrature_qr")
        .where("userId", "==", user.uid)
        .where("status", "==", "open")
        .limit(1)
        .get(),
    ]);

    const userData = userSnapshot.exists ? userSnapshot.data() : null;
    const openTimbratura = timbraturaSnapshot.empty
      ? null
      : {
          id: timbraturaSnapshot.docs[0].id,
          ...timbraturaSnapshot.docs[0].data(),
        };

    return {
      requiresCheck: userData?.anagrafica?.daVerificare === true,
      pauseHours: parsePauseHours(userData?.anagrafica?.pausa),
      openTimbratura,
    };
  }, [user?.uid]);

  const refreshAvailableCameras = useCallback(async () => {
    const cameras = await getAvailableVideoInputs();
    setAvailableCameras(cameras);
    return cameras;
  }, []);

  const getPreferredOrCurrentCameraId = useCallback(
    (cameras = []) => {
      if (selectedCameraId) {
        return selectedCameraId;
      }

      return choosePreferredCamera(cameras);
    },
    [selectedCameraId],
  );

  const requestCameraAccess = useCallback(async () => {
    if (cameraPermissionState === "granted") {
      await refreshAvailableCameras();
      return true;
    }

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== "function"
    ) {
      setScannerError(
        "Il dispositivo non espone una fotocamera compatibile per la scansione.",
      );
      setScannerStatus("unsupported");
      return false;
    }

    setScannerError("");
    setResult(null);
    setCameraPermissionState("requesting");
    setScannerStatus("requesting-permission");

    try {
      const stream = await getRearFacingStream();

      stream.getTracks().forEach((track) => track.stop());
      const cameras = await refreshAvailableCameras();
      const preferredCameraId = choosePreferredCamera(cameras);

      if (!selectedCameraId && preferredCameraId) {
        setSelectedCameraId(preferredCameraId);
      }

      setCameraPermissionState("granted");
      return true;
    } catch (error) {
      const errorName = error?.name || "";
      let message =
        "Impossibile accedere alla fotocamera. Verifica i permessi del browser e riprova.";

      if (
        errorName === "NotAllowedError" ||
        errorName === "PermissionDeniedError"
      ) {
        message =
          "Accesso alla fotocamera negato. Abilita il permesso dalle impostazioni di Safari e riprova.";
      } else if (
        errorName === "NotFoundError" ||
        errorName === "DevicesNotFoundError"
      ) {
        message =
          "Nessuna fotocamera disponibile su questo dispositivo per la scansione.";
      } else if (
        errorName === "NotReadableError" ||
        errorName === "TrackStartError" ||
        errorName === "AbortError"
      ) {
        message =
          "La fotocamera e gia in uso da un'altra app o non e disponibile in questo momento.";
      }

      setCameraPermissionState("denied");
      setScannerError(message);
      setScannerStatus("error");
      return false;
    }
  }, [cameraPermissionState, refreshAvailableCameras, selectedCameraId]);

  const handleClock = useCallback(
    async (rawValue) => {
      const sessionId = parseSessionId(rawValue);

      if (!sessionId || !selectedOperation || isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;
      setScannerStatus("submitting");
      setScannerError("");
      setResult(null);

      try {
        if (selectedOperation === "close") {
          const { requiresCheck, pauseHours, openTimbratura } =
            await loadCloseCheckContext();

          if (requiresCheck && openTimbratura?.openedAt?.toDate) {
            const openedAt = openTimbratura.openedAt.toDate();
            const now = new Date();
            const workedHours =
              (now.getTime() - openedAt.getTime()) / (1000 * 60 * 60) -
              pauseHours;

            if (workedHours < REQUIRED_DAILY_HOURS) {
              const missingHours = REQUIRED_DAILY_HOURS - workedHours;
              const shouldProceed = window.confirm(
                `Non hai ancora completato le 8 ore giornaliere. Mancano ${formatDurationForMessage(
                  missingHours,
                )} considerando una pausa di ${formatDurationForMessage(
                  pauseHours,
                )}. Vuoi chiudere comunque la timbratura?`,
              );

              if (!shouldProceed) {
                setResult({
                  type: "error",
                  title: "Chiusura annullata",
                  message:
                    "La timbratura resta aperta. Potrai chiuderla piu tardi.",
                });
                setScannerStatus("ready");
                return;
              }
            }
          }
        }

        const callable = functions.httpsCallable("clockViaTabletQr");
        const response = await callable({
          sessionId,
          operation: selectedOperation,
        });
        const data = response?.data || {};

        if (data?.ok) {
          setSuccessModal({
            title:
              selectedOperation === "open"
                ? "Ingresso registrato correttamente"
                : "Uscita registrata correttamente",
            message:
              selectedOperation === "open"
                ? "La timbratura di entrata e stata registrata con successo."
                : "La timbratura di uscita e stata registrata con successo.",
            confirmLabel: "Conferma",
          });
          setSelectedOperation("");
          stopScanner();
          return;
        }

        setResult({
          type: "error",
          title: "Operazione non completata",
          message:
            data?.message || "Si e verificato un errore durante la timbratura.",
        });
        setScannerStatus("ready");
      } catch (error) {
        setResult({
          type: "error",
          title: "Errore di timbratura",
          message:
            error?.message ||
            "Si e verificato un errore durante la timbratura.",
        });
        setScannerStatus("ready");
      } finally {
        lockTimeoutRef.current = setTimeout(() => {
          isSubmittingRef.current = false;
        }, SUBMIT_LOCK_DURATION_MS);
      }
    },
    [loadCloseCheckContext, parseSessionId, selectedOperation, stopScanner],
  );

  const handleCloseSuccessModal = useCallback(() => {
    setSuccessModal(null);
    setResult(null);
    setScannerError("");
  }, []);

  const handleSwitchCamera = useCallback(() => {
    if (!selectedOperation || availableCameras.length < 2) {
      return;
    }

    const cameraIds = availableCameras.map(getCameraId).filter(Boolean);
    if (cameraIds.length < 2) {
      return;
    }

    const currentCameraId =
      activeCameraId ||
      selectedCameraId ||
      choosePreferredCamera(availableCameras) ||
      cameraIds[0];
    const currentIndex = cameraIds.findIndex(
      (cameraId) => cameraId === currentCameraId,
    );
    const nextIndex =
      currentIndex >= 0 ? (currentIndex + 1) % cameraIds.length : 0;
    const nextCameraId = cameraIds[nextIndex];

    if (!nextCameraId || nextCameraId === currentCameraId) {
      return;
    }

    stopScanner();
    setScannerError("");
    setResult(null);
    setSelectedCameraId(nextCameraId);
  }, [
    activeCameraId,
    availableCameras,
    selectedCameraId,
    selectedOperation,
    stopScanner,
  ]);

  useEffect(() => {
    if (!selectedOperation) {
      stopScanner();
      return undefined;
    }

    let isCancelled = false;

    async function startHtml5Scanner() {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } =
          await import("html5-qrcode");

        if (isCancelled) return;

        setScannerMode("html5");
        setScannerError("");
        setResult(null);
        setScannerStatus("starting");
        await waitForNextPaint();

        const readerElement = document.getElementById(
          "timbratore-html5-reader",
        );
        if (!readerElement) {
          throw new Error(
            "HTML Element with id=timbratore-html5-reader not found",
          );
        }

        const scanner = new Html5Qrcode("timbratore-html5-reader");
        html5ScannerRef.current = scanner;

        const html5Config = {
          fps: 10,
          qrbox: { width: 220, height: 220 },
          aspectRatio: 1,
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        };

        const onDecode = async (decodedText) => {
          if (!decodedText || isSubmittingRef.current) return;
          await handleClock(decodedText);
        };

        const cameras = await Html5Qrcode.getCameras().catch(() => []);
        setAvailableCameras(cameras);
        const preferredCameraId = getPreferredOrCurrentCameraId(cameras);

        const startAttempts = [
          preferredCameraId,
          { facingMode: { exact: "environment" } },
          { facingMode: "environment" },
          { facingMode: { ideal: "environment" } },
        ].filter(Boolean);

        let lastStartError = null;

        for (const cameraConfig of startAttempts) {
          try {
            await scanner.start(cameraConfig, html5Config, onDecode, () => {});
            lastStartError = null;
            break;
          } catch (attemptError) {
            lastStartError = attemptError;
          }
        }

        if (lastStartError) {
          throw lastStartError;
        }

        const runningCameraId = scanner.getRunningTrackSettings?.()?.deviceId;
        setActiveCameraId(runningCameraId || preferredCameraId || "");

        if (isCancelled) {
          stopScanner();
          return;
        }

        setScannerStatus("ready");
      } catch {
        setScannerError(
          "Impossibile avviare il lettore QR su questo dispositivo. Verifica i permessi della fotocamera e riprova.",
        );
        setScannerStatus("error");
      }
    }

    async function startScanner() {
      if (typeof window === "undefined") {
        return;
      }

      if (typeof navigator === "undefined" || !navigator.mediaDevices) {
        setScannerError(
          "Il dispositivo non espone una fotocamera compatibile per la scansione.",
        );
        setScannerStatus("unsupported");
        return;
      }

      if (typeof window.BarcodeDetector === "undefined") {
        await startHtml5Scanner();
        return;
      }

      try {
        detectorRef.current = new window.BarcodeDetector({
          formats: ["qr_code"],
        });
        setScannerMode("native");
        setScannerError("");
        setResult(null);
        setScannerStatus("starting");

        const cameras = await refreshAvailableCameras();
        const preferredCameraId = getPreferredOrCurrentCameraId(cameras);
        const stream = await getRearFacingStream(preferredCameraId);

        streamRef.current = stream;

        if (isCancelled) {
          streamRef.current?.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
          return;
        }

        const activeTrack = streamRef.current?.getVideoTracks?.()[0];
        const runningCameraId = activeTrack?.getSettings?.().deviceId;
        setActiveCameraId(runningCameraId || preferredCameraId || "");

        if (videoRef.current) {
          videoRef.current.srcObject = streamRef.current;
          await videoRef.current.play();
        }

        setScannerStatus("ready");

        const scanFrame = async () => {
          if (isCancelled) return;

          frameRef.current = requestAnimationFrame(scanFrame);

          if (
            !videoRef.current ||
            videoRef.current.readyState < 2 ||
            !detectorRef.current ||
            isDetectingRef.current ||
            isSubmittingRef.current
          ) {
            return;
          }

          try {
            isDetectingRef.current = true;
            const codes = await detectorRef.current.detect(videoRef.current);
            if (codes?.length) {
              const rawValue = codes[0]?.rawValue;
              if (rawValue) {
                await handleClock(rawValue);
              }
            }
          } catch (error) {
            setScannerError(
              "Impossibile leggere il QR code. Riprova inquadrando meglio il codice.",
            );
          } finally {
            isDetectingRef.current = false;
          }
        };

        scanFrame();
      } catch {
        await startHtml5Scanner();
      }
    }

    startScanner();

    return () => {
      isCancelled = true;
      stopScanner();
    };
  }, [
    cameraPermissionState,
    getPreferredOrCurrentCameraId,
    handleClock,
    refreshAvailableCameras,
    selectedCameraId,
    selectedOperation,
    stopScanner,
  ]);

  const hasMultipleCameras = availableCameras.length > 1;

  return (
    <div className="timbratore-page">
      <div className="timbratore-actions">
        {OPERATIONS.map((operation) => {
          const isActive = selectedOperation === operation.value;
          return (
            <button
              key={operation.value}
              type="button"
              className={`timbratore-action-btn ${operation.value} ${isActive ? "active" : ""}`}
              onClick={async () => {
                stopScanner();
                setSelectedOperation("");
                const hasPermission = await requestCameraAccess();

                if (!hasPermission) {
                  setSelectedOperation("");
                  return;
                }

                setResult(null);
                setScannerError("");
                setSelectedOperation(operation.value);
              }}
            >
              {operation.title}
            </button>
          );
        })}
      </div>

      <div
        className={`timbratore-feedback-box ${
          scannerError
            ? "error"
            : result?.type === "error"
              ? "error"
              : "neutral"
        }`}
        role="status"
        aria-live={
          scannerError || result?.type === "error" ? "assertive" : "polite"
        }
      >
        {scannerError ? (
          <>
            <strong>Errore</strong>
            <span>{scannerError}</span>
          </>
        ) : result ? (
          <>
            <strong>{result.title}</strong>
            <span>{result.message}</span>
          </>
        ) : (
          <>
            <strong>
              {scannerStatus === "requesting-permission"
                ? "Richiesta autorizzazione"
                : scannerStatus === "starting"
                  ? "Avvio fotocamera"
                  : scannerStatus === "ready"
                    ? "Scanner attivo"
                    : scannerStatus === "submitting"
                      ? "Registrazione in corso"
                      : scannerStatus === "unsupported"
                        ? "Scanner non supportato"
                        : "In attesa"}
            </strong>
            <span>
              {selectedOperation
                ? "Avvicina il QR del tablet alla fotocamera."
                : scannerStatus === "requesting-permission"
                  ? "Conferma l'autorizzazione della fotocamera quando il browser la richiede."
                  : "Seleziona entra o esci per iniziare la scansione."}
            </span>
          </>
        )}
      </div>

      <div className="timbratore-reader-box">
        <div className="timbratore-reader-toolbar">
          <div className="timbratore-reader-status">
            {selectedOperation
              ? selectedOperation === "open"
                ? "Modalita entrata"
                : "Modalita uscita"
              : "Seleziona entra o esci"}
          </div>
          {selectedOperation && hasMultipleCameras ? (
            <button
              type="button"
              className="timbratore-switch-camera-btn"
              onClick={handleSwitchCamera}
            >
              Cambia telecamera
            </button>
          ) : null}
        </div>
        <div className="timbratore-video-wrap">
          {selectedOperation && scannerMode === "html5" ? (
            <div
              id="timbratore-html5-reader"
              className="timbratore-html5-reader"
            />
          ) : selectedOperation ? (
            <video
              ref={videoRef}
              className="timbratore-video"
              muted
              playsInline
            />
          ) : (
            <div className="timbratore-reader-placeholder">
              Inquadra il QR dopo aver scelto l&apos;operazione
            </div>
          )}
          <div className="timbratore-video-overlay">
            <div className="timbratore-scan-frame" />
          </div>
        </div>
      </div>

      {successModal ? (
        <div className="timbratore-modal-backdrop">
          <div
            className="timbratore-success-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="timbratore-success-title"
            aria-describedby="timbratore-success-message"
          >
            <div className="timbratore-success-icon" aria-hidden="true">
              ✓
            </div>
            <strong id="timbratore-success-title">{successModal.title}</strong>
            <p id="timbratore-success-message">{successModal.message}</p>
            <button
              type="button"
              className="timbratore-success-confirm-btn"
              onClick={handleCloseSuccessModal}
            >
              {successModal.confirmLabel}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
